import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AlertCircle, Loader2, Mail, ArrowLeft } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { apiService } from '../services/api';
import logger from '../utils/simpleLogger';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

interface AuthPanelProps {
  onAuthSuccess: (user: any, token?: string) => void;
}

type AuthView = 'main' | 'magic-link-form' | 'magic-link-sent' | 'verifying';

const AuthPanel: React.FC<AuthPanelProps> = ({ onAuthSuccess }) => {
  const { theme, themeMode } = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<AuthView>('main');
  const [email, setEmail] = useState('');
  const [gsiLoading, setGsiLoading] = useState(false);
  const [gsiButtonFallback, setGsiButtonFallback] = useState(false);
  const gsiLoadedRef = useRef(false);
  const gsiBtnRef = useRef<HTMLDivElement>(null);

  // Check for magic link verification token in URL (?verify=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Dev auto-login
    if (params.get('dev') === 'true') {
      setIsLoading(true);
      apiService.devLogin().then((result) => {
        if (result.success && result.user) {
          onAuthSuccess(result.user, result.access_token);
        } else {
          setError('Dev auto-login failed: ' + (result.error || 'unknown'));
          setIsLoading(false);
        }
      });
      return;
    }

    // Magic link verification
    const verifyToken = params.get('verify');
    if (verifyToken) {
      setView('verifying');
      setIsLoading(true);
      apiService.verifyMagicLink(verifyToken).then((result) => {
        if (result.success && result.user && result.access_token) {
          // Clean URL
          window.history.replaceState({}, '', window.location.pathname);
          onAuthSuccess(result.user, result.access_token);
        } else {
          setError(result.error || 'Error al verificar el link');
          setView('main');
          setIsLoading(false);
          window.history.replaceState({}, '', window.location.pathname);
        }
      });
    }
  }, [onAuthSuccess]);

  const handleGoogleCredential = useCallback(
    async (credential: string) => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await apiService.googleLogin(credential);
        if (response.success && response.user && response.access_token) {
          logger.info('[AUTH] Google login success', { userId: response.user.id });
          onAuthSuccess(response.user, response.access_token);
        } else {
          setError(response.error || 'Error al iniciar sesión con Google');
        }
      } catch (err) {
        logger.error('[AUTH] Google login error', err);
        setError('Error de conexión. Intenta de nuevo.');
      }
      setIsLoading(false);
    },
    [onAuthSuccess]
  );

  const loadGsiAndSignIn = useCallback(() => {
    if (!GOOGLE_CLIENT_ID || gsiLoading) return;
    setGsiLoading(true);
    setError(null);

    const initAndPrompt = () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const google = (window as any).google;
      if (!google?.accounts?.id) {
        setError('Error al cargar Google Sign-In');
        setGsiLoading(false);
        return;
      }
      google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (response: { credential?: string }) => {
          setGsiLoading(false);
          if (response.credential) {
            handleGoogleCredential(response.credential);
          } else {
            setError('No se recibió respuesta de Google');
          }
        },
        auto_select: false,
        use_fedcm_for_prompt: true,
      });
      google.accounts.id.prompt(
        (notification: { isNotDisplayed: () => boolean; isSkippedMoment: () => boolean }) => {
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            setGsiLoading(false);
            setGsiButtonFallback(true);
            if (gsiBtnRef.current) {
              google.accounts.id.renderButton(gsiBtnRef.current, {
                type: 'standard',
                theme: 'outline',
                size: 'large',
                width: gsiBtnRef.current.offsetWidth,
                text: 'continue_with',
              });
            }
          }
        }
      );
    };

    if (gsiLoadedRef.current) {
      initAndPrompt();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      gsiLoadedRef.current = true;
      initAndPrompt();
    };
    script.onerror = () => {
      setError('Error al cargar Google Sign-In');
      setGsiLoading(false);
    };
    document.head.appendChild(script);
  }, [gsiLoading, handleGoogleCredential]);

  const handleSendMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsLoading(true);
    setError(null);

    const result = await apiService.sendMagicLink(email.trim());

    if (result.success) {
      setView('magic-link-sent');
    } else {
      setError(result.error || 'Error al enviar el link');
    }
    setIsLoading(false);
  };

  const renderBetaError = () => (
    <div
      className={`mb-4 p-3 border rounded-lg flex items-start ${
        themeMode === 'dark'
          ? 'bg-amber-950/30 border-amber-800/50'
          : 'bg-amber-50 border-amber-200'
      }`}
    >
      <AlertCircle
        className={`w-4 h-4 mr-2 flex-shrink-0 mt-0.5 ${
          themeMode === 'dark' ? 'text-amber-400' : 'text-amber-600'
        }`}
      />
      <span className={`text-sm ${themeMode === 'dark' ? 'text-amber-200' : 'text-amber-800'}`}>
        Esta aplicación está en beta privada. Si quieres acceso anticipado, escríbenos a{' '}
        <a href="mailto:contacto@mychat.cl" className="underline font-medium">
          contacto@mychat.cl
        </a>
      </span>
    </div>
  );

  const renderError = () => {
    if (!error) return null;
    const isBeta =
      error.toLowerCase().includes('beta privada') ||
      error.toLowerCase().includes('acceso restringido');
    if (isBeta) return renderBetaError();
    return (
      <div
        className={`mb-4 p-3 border rounded-lg flex items-center ${
          themeMode === 'dark' ? 'bg-red-900/20 border-red-800/50' : 'bg-red-50 border-red-200'
        }`}
      >
        <AlertCircle className="w-4 h-4 text-red-500 mr-2 flex-shrink-0" />
        <span className={`text-sm ${themeMode === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
          {error}
        </span>
      </div>
    );
  };

  const renderMainView = () => (
    <>
      {GOOGLE_CLIENT_ID ? (
        <>
          <div
            ref={gsiBtnRef}
            className={`w-full min-h-[44px] flex items-center justify-center rounded-lg overflow-hidden ${
              gsiButtonFallback ? '' : 'hidden'
            }`}
          />
          {!gsiButtonFallback && (
            <button
              onClick={loadGsiAndSignIn}
              disabled={gsiLoading || isLoading}
              className="w-full flex items-center justify-center gap-3 py-2.5 px-4 rounded-lg border border-nb-border
                         text-sm text-nb-text hover:bg-nb-bg transition-colors min-h-[44px] disabled:opacity-50 cursor-pointer"
            >
              {gsiLoading ? (
                <span className="text-sm">Cargando...</span>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                    <path
                      fill="#EA4335"
                      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                    />
                    <path
                      fill="#4285F4"
                      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                    />
                    <path
                      fill="#34A853"
                      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                    />
                  </svg>
                  Continuar con Google
                </>
              )}
            </button>
          )}
        </>
      ) : (
        <p className="text-sm text-nb-text-secondary text-center">
          Google OAuth no configurado. Configura VITE_GOOGLE_CLIENT_ID.
        </p>
      )}

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-nb-border" />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="bg-nb-surface px-2 text-nb-text-secondary">o</span>
        </div>
      </div>

      <button
        onClick={() => {
          setView('magic-link-form');
          setError(null);
        }}
        className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg border border-nb-border
                   text-sm text-nb-text hover:bg-nb-bg transition-colors cursor-pointer"
      >
        <Mail className="w-4 h-4" />
        Continuar con email
      </button>
    </>
  );

  const renderMagicLinkForm = () => (
    <>
      <button
        onClick={() => {
          setView('main');
          setError(null);
        }}
        className="flex items-center gap-1 text-sm text-nb-text-secondary hover:text-nb-text mb-4 cursor-pointer"
      >
        <ArrowLeft className="w-4 h-4" />
        Volver
      </button>

      <form onSubmit={handleSendMagicLink} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-nb-text mb-1.5">
            Email
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tu@email.com"
            required
            autoFocus
            className="w-full px-3 py-2.5 rounded-lg border border-nb-border bg-nb-bg text-nb-text
                       text-sm placeholder:text-nb-text-secondary/50 focus:outline-none focus:ring-2
                       focus:ring-nb-accent/50 focus:border-nb-accent"
          />
        </div>
        <button
          type="submit"
          disabled={isLoading || !email.trim()}
          className="w-full py-2.5 px-4 rounded-lg bg-nb-accent text-white font-medium text-sm
                     hover:opacity-90 transition-opacity disabled:opacity-50 cursor-pointer"
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Enviando...
            </span>
          ) : (
            'Enviar link de acceso'
          )}
        </button>
      </form>
    </>
  );

  const renderMagicLinkSent = () => (
    <div className="text-center py-4">
      <div className="w-12 h-12 bg-nb-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
        <Mail className="w-6 h-6 text-nb-accent" />
      </div>
      <h3 className="text-lg font-medium text-nb-text mb-2">Revisa tu email</h3>
      <p className="text-sm text-nb-text-secondary mb-4">
        Enviamos un link de acceso a <strong className="text-nb-text">{email}</strong>. Expira en 10
        minutos.
      </p>
      <button
        onClick={() => {
          setView('main');
          setError(null);
          setEmail('');
        }}
        className="text-sm text-nb-accent hover:underline cursor-pointer"
      >
        Usar otro método
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-nb-bg flex flex-col">
      {/* Beta banner */}
      <div
        className={`w-full border-b py-1.5 text-center flex-shrink-0 ${
          themeMode === 'dark'
            ? 'bg-amber-950/30 border-amber-800/50'
            : 'bg-amber-50 border-amber-200'
        }`}
      >
        <p className={`text-xs ${themeMode === 'dark' ? 'text-amber-200' : 'text-amber-800'}`}>
          Aplicación en período de prueba. Acceso limitado a usuarios invitados.
        </p>
      </div>

      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-nb-accent rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                className={`${theme.colors.text.inverse} flex-shrink-0`}
                aria-hidden="true"
              >
                <path
                  d="M4 4h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H8l-4 4V6a2 2 0 0 1 2-2z"
                  fill="currentColor"
                  opacity="0.15"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <rect x="9" y="7" width="6" height="8" rx="1" fill="currentColor" opacity="0.6" />
                <line
                  x1="10.5"
                  y1="9.5"
                  x2="13.5"
                  y2="9.5"
                  stroke="var(--nb-bg)"
                  strokeWidth="1"
                  strokeLinecap="round"
                />
                <line
                  x1="10.5"
                  y1="11.5"
                  x2="13.5"
                  y2="11.5"
                  stroke="var(--nb-bg)"
                  strokeWidth="1"
                  strokeLinecap="round"
                />
                <line
                  x1="10.5"
                  y1="13.5"
                  x2="12.5"
                  y2="13.5"
                  stroke="var(--nb-bg)"
                  strokeWidth="1"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <h1 className="text-3xl font-semibold text-nb-text mb-2">MyChat</h1>
            <p className="text-nb-text-secondary">
              {view === 'magic-link-form'
                ? 'Ingresa tu email para recibir un link de acceso'
                : view === 'verifying'
                  ? 'Verificando tu acceso...'
                  : 'Accede con tu cuenta de Google o email'}
            </p>
          </div>

          {/* Card */}
          <div className="bg-nb-surface rounded-2xl shadow-xl border border-nb-border p-8 animate-fade-in">
            {renderError()}

            {isLoading && view === 'verifying' ? (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-nb-accent mb-3" />
                <p className="text-sm text-nb-text-secondary">Verificando link...</p>
              </div>
            ) : isLoading && view === 'main' ? (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-nb-accent mb-3" />
                <p className="text-sm text-nb-text-secondary">Iniciando sesión...</p>
              </div>
            ) : view === 'main' ? (
              renderMainView()
            ) : view === 'magic-link-form' ? (
              renderMagicLinkForm()
            ) : view === 'magic-link-sent' ? (
              renderMagicLinkSent()
            ) : null}
          </div>

          {/* Footer */}
          <div className="text-center mt-8">
            <p className="text-xs text-nb-text-secondary">Autenticación segura</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPanel;
